import { useEffect, useState } from "react";
import { motion } from "motion/react";
import ImageSlider from "../ui/ImageSlider";

function useIsDesktop(breakpointPx = 900) {
    const [isDesktop, setIsDesktop] = useState(() =>
        typeof window !== "undefined"
            ? window.matchMedia(`(min-width:${breakpointPx}px)`).matches
            : true
    );

    useEffect(() => {
        const mq = window.matchMedia(`(min-width:${breakpointPx}px)`);
        const handler = (e) => setIsDesktop(e.matches);

        mq.addEventListener?.("change", handler);
        mq.addListener?.(handler);

        setIsDesktop(mq.matches);

        return () => {
            mq.removeEventListener?.("change", handler);
            mq.removeListener?.(handler);
        };
    }, [breakpointPx]);

    return isDesktop;
}

export default function HeroSection({
                                        images,
                                        interval = 5000,
                                        styles,
                                        primaryColor = "#8D6E63",
                                        secondaryColor = "#A1887F",
                                        title = "Havaneser vom Schnüsterhus",
                                        subtitle = "Liebevolle Zucht mit Fokus auf Gesundheit, Wesen und Sozialisierung.",
                                        ctaPrimary = "Würfe ansehen",
                                        ctaSecondary = "Kontakt",

                                        // ✅ NEU: Ziel-Refs für die Buttons
                                        littersRef, // -> A-Wurf / Würfe Section (bei dir vermutlich puppiesRef)
                                        contactRef, // -> Kontakt Section
                                    }) {
    const isDesktop = useIsDesktop(900);

    const scrollTo = (ref) => {
        if (!ref?.current) return;
        ref.current.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            style={{
                ...styles.hero,
                height: isDesktop ? "86vh" : "70vh",
                minHeight: isDesktop ? 620 : 520,
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* Background Slider (OHNE Text) */}
            <div style={{ position: "absolute", inset: 0 }}>
                <ImageSlider images={images} interval={interval} styles={styles} />
            </div>

            {/* Dark overlay für Lesbarkeit (kein Glas) */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 2,
                    background: isDesktop
                        ? "linear-gradient(90deg, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.18) 100%)"
                        : "linear-gradient(180deg, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.28) 70%, rgba(0,0,0,0.18) 100%)",
                }}
            />

            {/* Content */}
            <div
                style={{
                    position: "relative",
                    zIndex: 3,
                    height: "100%",
                    maxWidth: 1200,
                    margin: "0 auto",
                    padding: isDesktop ? "0 20px" : "0 18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isDesktop ? "flex-start" : "center",
                }}
            >
                <div
                    style={{
                        width: "100%",
                        maxWidth: isDesktop ? 640 : 520,
                        textAlign: isDesktop ? "left" : "center",
                    }}
                >
                    <motion.h1
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        style={{
                            margin: 0,
                            color: "#fff",
                            fontFamily: "'Playfair Display', serif",
                            fontSize: isDesktop ? "3.2rem" : "2.2rem",
                            lineHeight: 1.12,
                            letterSpacing: "0.2px",
                            textShadow: "0 10px 28px rgba(0,0,0,0.35)",
                        }}
                    >
                        {title}
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.08 }}
                        style={{
                            marginTop: 14,
                            marginBottom: 22,
                            color: "rgba(255,255,255,0.92)",
                            fontSize: isDesktop ? "1.15rem" : "1.05rem",
                            lineHeight: 1.7,
                            textShadow: "0 8px 22px rgba(0,0,0,0.25)",
                        }}
                    >
                        {subtitle}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.16 }}
                        style={{
                            display: "flex",
                            flexDirection: isDesktop ? "row" : "column",
                            gap: 12,
                            justifyContent: isDesktop ? "flex-start" : "center",
                            alignItems: "center",
                        }}
                    >
                        <motion.button
                            whileHover={{ scale: 1.03, backgroundColor: secondaryColor }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => scrollTo(littersRef)}
                            style={{
                                border: "none",
                                cursor: "pointer",
                                padding: "12px 18px",
                                borderRadius: 999,
                                color: "#fff",
                                fontWeight: 800,
                                fontSize: 15,
                                backgroundColor: primaryColor,
                                boxShadow: "0 14px 35px rgba(0,0,0,0.28)",
                                width: isDesktop ? "auto" : "100%",
                                maxWidth: 360,
                                transition: "background-color 0.2s ease",
                            }}
                        >
                            {ctaPrimary}
                        </motion.button>

                        <motion.button
                            whileHover={{ scale: 1.03, borderColor: "rgba(255,255,255,0.95)" }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => scrollTo(contactRef)}
                            style={{
                                cursor: "pointer",
                                padding: "12px 18px",
                                borderRadius: 999,
                                fontWeight: 800,
                                fontSize: 15,
                                backgroundColor: "transparent",
                                color: "#fff",
                                border: "1.5px solid rgba(255,255,255,0.65)",
                                width: isDesktop ? "auto" : "100%",
                                maxWidth: 360,
                            }}
                        >
                            {ctaSecondary}
                        </motion.button>
                    </motion.div>
                </div>
            </div>

            {/* ✅ Pfeil/Scroll-Indicator entfernt */}
        </section>
    );
}
